import * as Yup from 'yup';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

// @mui
import { Link, Stack, Alert, IconButton, InputAdornment } from '@mui/material';

import { LoadingButton } from '@mui/lab';

// routes
import { PATH_AUTH_CLIENT } from '../../../../routes/paths';
// hooks
import useAuth from '../../../../hooks/useAuth';
// portal <-> account-type mapping
import { PORTAL_TYPES } from '../../../../contexts/JWTContext';
// components
import Icon from '../../../../components/universe/Icon';
import FormProvider from '../../../../components/hook-form/FormProvider';
import RHFTextField from '../../../../components/hook-form/RHFTextField';
import {
  authFieldSx,
  authButtonSx,
  authLinkSx
} from '../../../../layouts/auth/portals';
// ----------------------------------------------------------------------


export default function LoginForm({ setEmail, setIsVerified }) {
  const { signInWithPassword } = useAuth();

  const [showPassword, setShowPassword] = useState(false);
  const [success] = useState(false);
  const [authError, setAuthError] = useState('');
  const [wrongPortal, setWrongPortal] = useState(null);
  const LoginSchema = Yup.object().shape({
    email: Yup.string()
      .email("L'email deve essere un indirizzo email valido")
      .required("L'email è obbligatoria"),
    password: Yup.string().required('La password è obbligatoria')
  });

  const defaultValues = {
    email: '',
    password: ''
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues
  });

  const {
    handleSubmit,
    formState: { isSubmitting }
  } = methods;

  const onSubmit = async (data) => {
    setAuthError('');
    setWrongPortal(null);
    try {
      await signInWithPassword(data.email, data.password, PORTAL_TYPES.clients);
      // Session established; AuthProvider + ClientGuestGuard route onward.
    } catch (err) {
      // Supabase returns "Email not confirmed" for a registered-but-unverified
      // account — hand off to the "check your email" screen in that case.
      if (/confirm/i.test(err?.message || '')) {
        setEmail(data.email);
        setIsVerified(false);
        return;
      }
      setAuthError('Inserisci una password valida');
      // Valid credentials for another portal: offer the way there.
      if (err?.portalPath) {
        setAuthError(err.message);
        setWrongPortal({ path: err.portalPath, label: err.portalLabel });
      }
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {authError && (
          <Alert
            severity={wrongPortal ? 'info' : 'error'}
            action={
              wrongPortal ? (
                <Link
                  component={RouterLink}
                  to={wrongPortal.path}
                  underline="hover"
                  sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
                >
                  Vai a {wrongPortal.label}
                </Link>
              ) : null
            }
          >
            {authError}
          </Alert>
        )}
        {success && (
          <Alert severity="success">
            Ti verrà inviata un'e-mail per la verifica
          </Alert>
        )}
        <RHFTextField
          sx={authFieldSx}
          name="email"
          label="Email address"
        />
        <RHFTextField
          name="password"
          label="Password"
          sx={authFieldSx}
          type={showPassword ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  <Icon name={showPassword ? 'Eye' : 'EyeSlash'} size="md" />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </Stack>
      <LoadingButton
        fullWidth
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitting}
        sx={(theme) => ({ ...authButtonSx(theme), mt: 3 })}
      >
        <span className="notranslate" translate="no">
          Accedi
        </span>
      </LoadingButton>

      {/* Ogni alternativa ha la sua pagina: questa schermata resta con una sola
          azione principale e nessun flusso condivide lo stato del form. */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mt: 2.5 }}
      >
        <Link
          component={RouterLink}
          to={PATH_AUTH_CLIENT.magicLink}
          underline="hover"
          sx={authLinkSx}
        >
          Accedi con un link
        </Link>
        <Link
          component={RouterLink}
          to={PATH_AUTH_CLIENT.resetPassword}
          underline="hover"
          sx={(theme) => authLinkSx(theme, { quiet: true })}
        >
          Password dimenticata?
        </Link>
      </Stack>
    </FormProvider>
  );
}
